import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { getOnboardingStatus } from '../../services/doctor-onboarding/doctorOnboardingService';
import { Header } from '../../../../shared/components';
import '../../../../shared/styles/common.css';
import '../../../../shared/components/Header/Header.css';
import './LoginPage.css';

declare global {
  interface Window {
    google: any;
  }
}

/* Left panel: AI healthcare network graphic (lucid) */
const AIHealthcareGraphic = () => (
  <svg
    className="login-left-graphic"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <defs>
      <linearGradient id="sphereGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(147, 197, 253, 0.4)" />
        <stop offset="100%" stopColor="rgba(94, 234, 212, 0.3)" />
      </linearGradient>
    </defs>
    {/* Sphere outline / network nodes */}
    <circle cx="100" cy="85" r="55" stroke="rgba(147, 197, 253, 0.5)" strokeWidth="0.8" fill="none" />
    <circle cx="100" cy="85" r="42" stroke="rgba(147, 197, 253, 0.35)" strokeWidth="0.6" fill="none" />
    {/* Cross lines */}
    {[45, 70, 100, 130, 155].map((y) => (
      <line key={y} x1="50" y1={y} x2="150" y2={y} stroke="rgba(147, 197, 253, 0.4)" strokeWidth="0.5" />
    ))}
    {[55, 75, 100, 125, 145].map((x) => (
      <line key={x} x1={x} y1="35" x2={x} y2="135" stroke="rgba(147, 197, 253, 0.4)" strokeWidth="0.5" />
    ))}
    {/* Dots */}
    {[60, 80, 100, 120, 140].map((x, i) =>
      [50, 70, 85, 100, 120].map((y, j) => (
        <circle key={`${i}-${j}`} cx={x} cy={y} r="2" fill="rgba(147, 197, 253, 0.8)" />
      ))
    )}
  </svg>
);

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleInitializedRef = useRef(false);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const redirectAfterDoctorLogin = useCallback(async () => {
    try {
      const status = await getOnboardingStatus();
      const statusVal = (status.onboarding_status || '').toLowerCase();
      if (statusVal === 'approved') {
        navigate('/dashboard');
      } else if (statusVal === 'submitted' || statusVal === 'rejected') {
        navigate('/doctor-review-status');
      } else {
        navigate('/doctor-onboarding');
      }
    } catch {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleGoogleSignIn = useCallback(async (response: any) => {
    const credential = response.credential || response;
    if (!credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const loginResponse = await authService.loginWithGoogle(credential);
      authService.storeTokens(
        loginResponse.access_token,
        loginResponse.refresh_token,
        loginResponse.csrf_token
      );
      await redirectAfterDoctorLogin();
    } catch (err: any) {
      console.error('Google login error:', err);
      setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [redirectAfterDoctorLogin]);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'session_expired') {
      setError('Your session has expired. Please log in again.');
      setSearchParams((params) => {
        params.delete('error');
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      redirectAfterDoctorLogin().catch(() => navigate('/dashboard'));
      return;
    }
    const initializeGoogleSignIn = () => {
      if (googleInitializedRef.current || !window.google || !googleClientId || !googleButtonRef.current) return;
      googleInitializedRef.current = true;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleSignIn,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 280,
        text: 'signin_with',
        locale: 'en',
        shape: 'rectangular',
      });
    };
    let checkInterval: NodeJS.Timeout | null = null;
    if (window.google) initializeGoogleSignIn();
    else {
      checkInterval = setInterval(() => {
        if (window.google && checkInterval) clearInterval(checkInterval);
        if (window.google) initializeGoogleSignIn();
      }, 100);
      setTimeout(() => { if (checkInterval) clearInterval(checkInterval); }, 10000);
    }
    return () => { if (checkInterval) clearInterval(checkInterval); };
  }, [googleClientId, handleGoogleSignIn, navigate, redirectAfterDoctorLogin]);

  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const loginResponse = await authService.loginWithEmailPassword(email, password);
      authService.storeTokens(
        loginResponse.access_token,
        loginResponse.refresh_token,
        loginResponse.csrf_token
      );
      await redirectAfterDoctorLogin();
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <Header showDietitiansLink={false} />
      <div className="login-page">
        <div className="login-card">
          {/* Left panel: dark teal + AI graphic */}
          <div className="login-card-left">
            <AIHealthcareGraphic />
            <p className="login-card-left-line1">AI HEALTHCARE</p>
            <p className="login-card-left-line2">NETWORK</p>
          </div>
          {/* Right panel: white form */}
          <div className="login-card-right">
            <div className="login-brand">
              <img src="/images/zivo_logo.png" alt="Zivo" className="login-logo-img" />
            </div>
            <h1 className="login-title">Log in to Zivohealth</h1>
            <p className="login-subtitle">Precision Nutrition for Healthcare is on its way.</p>

            <div className="login-form-container">
              {googleClientId && (
                <>
                  <div ref={googleButtonRef} className="google-signin-button" />
                  <div className="divider">
                    <span className="divider-line" />
                    <span className="divider-text">OR</span>
                    <span className="divider-line" />
                  </div>
                </>
              )}

              <form onSubmit={handleEmailPasswordLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">EMAIL ADDRESS</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@hospital.com"
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <div className="password-label-row">
                    <label htmlFor="password">PASSWORD</label>
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={() => navigate('/forgot-password')}
                      disabled={loading}
                    >
                      FORGOT?
                    </button>
                  </div>
                  <div className="password-input-container">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {error && <div className="error-message">{error}</div>}
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log In'}
                </button>
                <p className="signup-link">
                  New to Zivo? <Link to="/doctor-signup">Create account</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
